import React, { useEffect, useRef, Suspense, lazy, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useAuthStore } from './store/authStore';
import { initTabCommunication, cleanupTabCommunication } from './lib/tabCommunication';
import { usePushSubscriptionSync } from './hooks/usePushSubscriptionSync';
import LoadingSpinner from './components/ui/LoadingSpinner';
import NewLandingPage from './components/landing/NewLandingPage';
import TimeTogetherCalculator from './components/landing/TimeTogetherCalculator';
import Dashboard from './components/dashboard/Dashboard';
import * as Sentry from '@sentry/react';
import ErrorBoundary from './components/error/ErrorBoundary';
import { NavigationProvider } from './contexts/NavigationContext';
import { logger } from './lib/devLogger';

const StoryRecorder = lazy(() => import('./components/recorder/StoryRecorder'));
const AdminPanel = lazy(() => import('./components/admin/AdminPanel'));
const InterviewLab = lazy(() => import('./components/admin/InterviewLab'));
const AcceptInvitationPage = lazy(() => import('./components/invitation/AcceptInvitationPage'));
const ResetPasswordPage = lazy(() => import('./components/auth/ResetPasswordPage'));
const MemoirPreview = lazy(() => import('./components/memoir/MemoirPreview'));
const PrivacyPolicyPage = lazy(() => import('./components/legal/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./components/legal/TermsOfServicePage'));
const TypographyPreview = lazy(() => import('./components/dev/TypographyPreview'));
const ActivatePushPage = lazy(() => import('./components/notifications/ActivatePushPage'));
const ListenView = lazy(() => import('./components/listen/ListenView'));

const ResetPasswordRoute: React.FC = () => {
  const { loading } = useAuthStore();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" message="Cargando..." />
      </div>
    );
  }

  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="lg" message="Cargando..." /></div>}>
      <ResetPasswordPage />
    </Suspense>
  );
};

// Sessionstorage key that holds the URL the user was trying to reach
// before being bounced to the landing page for auth. Used so that an
// email-reminder deep-link survives the login round-trip.
const PROTECTED_REDIRECT_KEY = 'ethernal_protected_redirect';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuthStore();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" message="Cargando..." />
      </div>
    );
  }

  if (!user) {
    try {
      const target = `${location.pathname}${location.search}`;
      // Don't store generic landing/dashboard targets — only deep links
      // that the user wouldn't reach naturally after dashboard load.
      if (target && target !== '/' && target !== '/dashboard') {
        sessionStorage.setItem(PROTECTED_REDIRECT_KEY, target);
      }
    } catch {
      // sessionStorage unavailable (private mode) — non-fatal, fallback
      // is dashboard.
    }
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Pages only an administrator may see (02-11, Cristian: the interview lab was reachable by any signed-in
// user; the server already refused them, but the page itself showed). Goes INSIDE ProtectedRoute, so
// the user is loaded; `isAdmin` is set in the same store update as `user`.
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAdmin } = useAuthStore();
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

const AuthenticatedRedirect: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuthStore();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app-bg">
        <LoadingSpinner size="lg" message="Inicializando aplicación..." />
      </div>
    );
  }

  if (user) {
    let target = '/dashboard';
    try {
      const saved = sessionStorage.getItem(PROTECTED_REDIRECT_KEY);
      if (saved) {
        sessionStorage.removeItem(PROTECTED_REDIRECT_KEY);
        target = saved;
      }
    } catch {
      // sessionStorage unavailable — fall through to dashboard.
    }
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
};

function App() {
  const { initialize, cleanup, loading, user } = useAuthStore();
  const initializedRef = useRef(false);

  // Mirror any active push subscription into push_subscriptions on every
  // load. Covers iPhone users completing Add-to-Home-Screen + grant flow
  // outside the modal, and re-installs / new devices that need the
  // backend row re-created.
  usePushSubscriptionSync(user?.id);
  useEffect(() => {
    // Prevent double initialization (React StrictMode, hot reload, etc.)
    if (initializedRef.current) {
      console.log('[App] Already initialized, skipping duplicate initialization');
      return;
    }

    initializedRef.current = true;
    console.log('[App] Initializing application for first time');

    // Initialize tab communication
    const tabId = initTabCommunication();
    console.log('[App] Tab communication initialized:', tabId);

    // Initialize auth
    initialize();

    // Cleanup on unmount
    return () => {
      console.log('[App] Cleaning up on unmount');
      cleanup();
      cleanupTabCommunication();
    };
  }, []); // Empty deps - run ONCE only

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app-bg">
        <LoadingSpinner size="lg" message="Inicializando aplicación..." />
      </div>
    );
  }

  return (
    <ErrorBoundary level="global" onError={(error, errorInfo) => {
      console.error('[App] Global error caught:', error, errorInfo);
      Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    }}>
      <Router>
        <NavigationProvider>
        <Routes>
          <Route
            path="/"
            element={
              <AuthenticatedRedirect>
                <NewLandingPage />
              </AuthenticatedRedirect>
            }
          />
          <Route
            path="/calculadora"
            element={
              <AuthenticatedRedirect>
                <TimeTogetherCalculator />
              </AuthenticatedRedirect>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <ErrorBoundary level="route" componentName="Dashboard">
                  <Dashboard />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/story-recorder/:storyId"
            element={
              <ProtectedRoute>
                <ErrorBoundary level="route" componentName="StoryRecorder">
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="lg" message="Cargando grabadora..." /></div>}>
                    <StoryRecorder />
                  </Suspense>
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <ErrorBoundary level="route" componentName="AdminPanel">
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="lg" message="Cargando panel de administración..." /></div>}>
                    <AdminPanel />
                  </Suspense>
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/interview-lab"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <ErrorBoundary level="route" componentName="InterviewLab">
                    <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="lg" message="Cargando laboratorio..." /></div>}>
                      <InterviewLab />
                    </Suspense>
                  </ErrorBoundary>
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/memoir/:storyId"
            element={
              <ProtectedRoute>
                <ErrorBoundary level="route" componentName="MemoirPreview">
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="lg" message="Cargando memorias..." /></div>}>
                    <MemoirPreview />
                  </Suspense>
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/accept-invitation"
            element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="lg" message="Cargando invitación..." /></div>}>
                <AcceptInvitationPage />
              </Suspense>
            }
          />
          <Route
            path="/reset-password"
            element={<ResetPasswordRoute />}
          />
          <Route
            path="/privacidad"
            element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="lg" message="Cargando..." /></div>}>
                <PrivacyPolicyPage />
              </Suspense>
            }
          />
          <Route
            path="/terminos"
            element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="lg" message="Cargando..." /></div>}>
                <TermsOfServicePage />
              </Suspense>
            }
          />
          <Route
            path="/preview-typography"
            element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="lg" message="Cargando..." /></div>}>
                <TypographyPreview />
              </Suspense>
            }
          />
          <Route
            path="/activate-push"
            element={
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="lg" message="Cargando..." /></div>}>
                <ActivatePushPage />
              </Suspense>
            }
          />
          <Route
            path="/story/:storyId/listen/:questionId"
            element={
              <ProtectedRoute>
                <ErrorBoundary level="route" componentName="ListenView">
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="lg" message="Cargando..." /></div>}>
                    <ListenView />
                  </Suspense>
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" />
        </NavigationProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;